import React from 'react';

import BadgeCDS from '@sharedCR/coreDesignSystem/BadgeCDS';
import { BadgeColorVariant } from '@sharedCR/coreDesignSystem/BadgeCDS/BadgeCDS.constants';

import { alwaysTrue, getConditionalObject } from 'utils/data';

import DrawRequestActionButton from './DrawRequests/ActionButton';
import ActionButton from './EscrowActivity/ActionButton';
import PaymentActionButton from './PaymentHistory/ActionButton';
import ServiceLogActionButton from './ServiceLog/ActionButton';
import TypeIcon from './ServiceLog/TypeIcon';
import {
  DisbursementTypeLabel,
  DrawRequestStatusEnum,
  DrawRequestStatusLabel,
  FollowUpStatusLabel,
  PaymentMethodLabel,
  PaymentPhaseLabel,
  PaymentStatusEnum,
  PaymentStatusLabel,
  ScheduleStatusEnum,
  ScheduleStatusLabel,
} from './Servicing.constants';
import { formatCurrency, formatDateValue } from './Servicing.utils';

export const getEscrowDisbursementsTableConfig = ({ onEdit, onDelete, isLoading }) => ({
  disbursementDate: {
    label: 'Disbursement Date',
    width: '15%',
    content: row => formatDateValue(row.disbursementDate),
    renderContentAsText: alwaysTrue,
  },
  disbursementType: {
    label: 'Type',
    width: '20%',
    content: ({ disbursementType }) => DisbursementTypeLabel[disbursementType] ?? '-',
    renderContentAsText: alwaysTrue,
  },
  payeeName: {
    label: 'Payee',
    width: '15%',
    content: row => row.payeeName || '-',
    renderContentAsText: alwaysTrue,
  },
  amount: {
    label: 'Amount',
    width: '15%',
    content: row => formatCurrency(row.amount),
    renderContentAsText: alwaysTrue,
    align: 'right',
  },
  referenceNumber: {
    label: 'Reference #',
    width: '10%',
    content: row => row.referenceNumber || '-',
    renderContentAsText: alwaysTrue,
  },
  notes: {
    label: 'Notes',
    width: '15%',
    content: row => row.notes || '-',
    renderContentAsText: alwaysTrue,
  },
  actions: {
    label: '',
    width: '10%',
    content: row => (
      <ActionButton disabled={isLoading} row={row} onDelete={onDelete} onEdit={onEdit} />
    ),
  },
});

const PAYMENT_STATUS_COLOR_MAP = {
  [PaymentStatusEnum.APPLIED]: BadgeColorVariant.GREEN,
  [PaymentStatusEnum.REVERSED]: BadgeColorVariant.RED,
  [PaymentStatusEnum.PENDING]: BadgeColorVariant.ORANGE,
  [PaymentStatusEnum.NSF]: BadgeColorVariant.RED,
};

const DRAW_REQUEST_STATUS_COLOR_MAP = {
  [DrawRequestStatusEnum.PENDING]: BadgeColorVariant.ORANGE,
  [DrawRequestStatusEnum.APPROVED]: BadgeColorVariant.GREEN,
  [DrawRequestStatusEnum.DENIED]: BadgeColorVariant.RED,
  [DrawRequestStatusEnum.CANCELLED]: BadgeColorVariant.GREY,
};

const SCHEDULE_STATUS_COLOR_MAP = {
  [ScheduleStatusEnum.UPCOMING]: BadgeColorVariant.BRAND,
  [ScheduleStatusEnum.PAID]: BadgeColorVariant.GREEN,
  [ScheduleStatusEnum.PAST_DUE]: BadgeColorVariant.RED,
  [ScheduleStatusEnum.PARTIAL]: BadgeColorVariant.ORANGE,
};

export const getPaymentScheduleTableConfig = ({ isHeloc, hasEscrow = false } = {}) => ({
  dueDate: {
    label: 'Due Date',
    width: isHeloc ? '13%' : '13%',
    content: row => formatDateValue(row.dueDate),
    renderContentAsText: alwaysTrue,
  },
  ...getConditionalObject(isHeloc, {
    paymentPhase: {
      label: 'Phase',
      width: '12%',
      content: ({ paymentPhase }) => PaymentPhaseLabel[paymentPhase] ?? '-',
      renderContentAsText: alwaysTrue,
    },
  }),
  principalDue: {
    label: 'Principal',
    width: isHeloc ? '13%' : '13%',
    content: row => formatCurrency(row.principalDue),
    renderContentAsText: alwaysTrue,
    align: 'right',
  },
  interestDue: {
    label: 'Interest',
    width: isHeloc ? '13%' : '13%',
    content: row => formatCurrency(row.interestDue),
    renderContentAsText: alwaysTrue,
    align: 'right',
  },
  ...getConditionalObject(hasEscrow, {
    escrowDue: {
      label: 'Escrow',
      width: '13%',
      content: row => formatCurrency(row.escrowDue),
      renderContentAsText: alwaysTrue,
      align: 'right',
    },
  }),
  totalDue: {
    label: 'Total Due',
    width: isHeloc ? '11%' : '13%',
    content: row => formatCurrency(row.totalDue),
    renderContentAsText: alwaysTrue,
    align: 'right',
  },
  remainingBalance: {
    label: 'Principal Balance',
    width: isHeloc ? '13%' : '13%',
    content: row => formatCurrency(row.remainingBalance),
    renderContentAsText: alwaysTrue,
    align: 'right',
  },
  status: {
    label: 'Status',
    width: isHeloc ? '12%' : '14%',
    content: ({ status }) => (
      <BadgeCDS
        colorVariant={SCHEDULE_STATUS_COLOR_MAP[status]}
        value={ScheduleStatusLabel[status] ?? '-'}
      />
    ),
  },
});

export const getServiceLogTableConfig = ({ onEdit, onDelete, isLoading }) => ({
  interactionDate: {
    label: 'Date',
    width: '12%',
    content: row => formatDateValue(row.interactionDate),
    renderContentAsText: alwaysTrue,
  },
  interactionType: {
    label: 'Type',
    width: '12%',
    content: row => <TypeIcon row={row} />,
  },
  subject: {
    label: 'Subject',
    width: '15%',
    content: row => row.subject || '-',
    renderContentAsText: alwaysTrue,
  },
  contactName: {
    label: 'Contact Name',
    width: '14%',
    content: row => row.contactName || '-',
    renderContentAsText: alwaysTrue,
  },
  resolution: {
    label: 'Resolution',
    width: '15%',
    content: row => row.resolution || '-',
    renderContentAsText: alwaysTrue,
  },
  followUpDate: {
    label: 'Follow-Up Date',
    width: '12%',
    content: row => formatDateValue(row.followUpDate),
    renderContentAsText: alwaysTrue,
  },
  followUpCompleted: {
    label: 'Follow-Up Status',
    width: '10%',
    content: ({ followUpDate, followUpCompleted }) => {
      if (!followUpDate) {
        return '-';
      }

      return (
        <BadgeCDS
          colorVariant={followUpCompleted ? BadgeColorVariant.GREEN : BadgeColorVariant.ORANGE}
          value={FollowUpStatusLabel[followUpCompleted] ?? '-'}
        />
      );
    },
  },
  actions: {
    label: '',
    width: '4%',
    content: row => (
      <ServiceLogActionButton disabled={isLoading} row={row} onDelete={onDelete} onEdit={onEdit} />
    ),
  },
});

export const getPaymentHistoryTableConfig = ({
  onEdit,
  onDelete,
  onReverse,
  isLoading,
  hasEscrow = false,
} = {}) => ({
  paymentDate: {
    label: 'Payment Date',
    width: '10%',
    content: row => formatDateValue(row.paymentDate),
    renderContentAsText: alwaysTrue,
  },
  effectiveDate: {
    label: 'Effective Date',
    width: '10%',
    content: row => formatDateValue(row.effectiveDate),
    renderContentAsText: alwaysTrue,
  },
  totalAmount: {
    label: 'Total Amount',
    width: '10%',
    content: row => formatCurrency(row.totalAmount),
    renderContentAsText: alwaysTrue,
    align: 'right',
  },
  principalAmount: {
    label: 'Principal',
    width: '10%',
    content: row => formatCurrency(row.principalAmount),
    renderContentAsText: alwaysTrue,
    align: 'right',
  },
  interestAmount: {
    label: 'Interest',
    width: '10%',
    content: row => formatCurrency(row.interestAmount),
    renderContentAsText: alwaysTrue,
    align: 'right',
  },
  ...getConditionalObject(hasEscrow, {
    escrowAmount: {
      label: 'Escrow',
      width: '10%',
      content: row => formatCurrency(row.escrowAmount),
      renderContentAsText: alwaysTrue,
      align: 'right',
    },
  }),
  runningPrincipalBalance: {
    label: 'Principal Balance',
    width: '10%',
    content: row => formatCurrency(row.runningPrincipalBalance),
    renderContentAsText: alwaysTrue,
    align: 'right',
  },
  paymentMethod: {
    label: 'Method',
    width: '10%',
    content: ({ paymentMethod }) => PaymentMethodLabel[paymentMethod] ?? '-',
    renderContentAsText: alwaysTrue,
  },
  paymentStatus: {
    label: 'Status',
    width: '8%',
    content: ({ paymentStatus }) => (
      <BadgeCDS
        colorVariant={PAYMENT_STATUS_COLOR_MAP[paymentStatus]}
        value={PaymentStatusLabel[paymentStatus] ?? '-'}
      />
    ),
  },
  ...getConditionalObject(onEdit, {
    actions: {
      label: '',
      width: '4%',
      content: row => (
        <PaymentActionButton
          disabled={isLoading}
          row={row}
          onDelete={onDelete}
          onEdit={onEdit}
          onReverse={onReverse}
        />
      ),
    },
  }),
});

export const getDrawRequestsTableConfig = ({ onEdit, isLoading }) => ({
  requestDate: {
    label: 'Request Date',
    width: '15%',
    content: row => formatDateValue(row.requestDate),
    renderContentAsText: alwaysTrue,
  },
  requestedAmount: {
    label: 'Requested Amount',
    width: '18%',
    content: row => formatCurrency(row.requestedAmount),
    renderContentAsText: alwaysTrue,
    align: 'right',
  },
  approvedAmount: {
    label: 'Approved Amount',
    width: '18%',
    content: row => formatCurrency(row.approvedAmount),
    renderContentAsText: alwaysTrue,
    align: 'right',
  },
  status: {
    label: 'Status',
    width: '12%',
    content: ({ status }) => (
      <BadgeCDS
        colorVariant={DRAW_REQUEST_STATUS_COLOR_MAP[status]}
        value={DrawRequestStatusLabel[status] ?? '-'}
      />
    ),
  },
  processedDate: {
    label: 'Processed Date',
    width: '15%',
    content: row => formatDateValue(row.processedDate),
    renderContentAsText: alwaysTrue,
  },
  notes: {
    label: 'Notes',
    width: '14%',
    content: row => row.notes || '-',
    renderContentAsText: alwaysTrue,
  },
  actions: {
    label: '',
    width: '8%',
    content: row => <DrawRequestActionButton disabled={isLoading} row={row} onEdit={onEdit} />,
  },
});
